/**
 * Investor-Portal — Segment EBITDA-Übersicht (#1180 v2.565.0, #1181 v2.566.0).
 *
 * Visuelles Abstract der Segment-Analyse:
 *   Σ 5 Segment-DB  −  Gemeinkosten-Block  =  errechnetes EBITDA
 *                    Reconciliation vs. GuV-EBITDA (UGB-Pivot)
 *
 * Quelle:
 *   - Page-DB der 5 Segment-Services (Outbound/Inbound/Lager/Carrier/Material)
 *   - Gemein-KS [6, 7, 8, 45] aus Odoo account.analytic.line
 *   - GuV-EBITDA aus investor_ist_values (UGB-Sync)
 *
 * #1181: Drei umschaltbare Chart-Modi, Default „Geldstrom".
 *   - flow (Default): Schrumpfende horizontale Balken Umsatz → EBITDA
 *   - funnel: Trichter-SVG für Pitch/Shareholder
 *   - waterfall: bestehender Wasserfall (Experten-Sicht)
 *
 * Read-only Reporting. Differenz wird NICHT versteckt — als Badge
 * „⚠️ Odoo-Bereinigung ausstehend" sichtbar gelabelt.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, ComposedChart, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceLine, LabelList,
} from 'recharts';
import { TrendingUp, Wallet, BarChart3, Scale, Droplets, Funnel, BarChartHorizontal } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v); }
function fmtEurDetail(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtPct(v) { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(1)} %`; }

export default function InvestorSegmentEbitda() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState('flow'); // 'flow' | 'funnel' | 'waterfall'

  // #1184 v2.569.0 — YTD frontend-seitig auf belastbaren Cutoff verkürzen
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/ebitda?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); } else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodQuery]);

  // #1206 — 6-Stationen-Brücke (Manuel-Entscheidung A):
  //   Σ Segment-DB → +Sonstige Erträge → −Sonstige Aufwände → −Gemeinkosten → +GL-Differenz → errechnetes EBITDA → GuV-EBITDA
  // ComposedChart mit unsichtbarem "base"-Stack + sichtbarer "delta"-Stack → klassisches Waterfall-Pattern.
  const waterfallData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    let cum = 0;
    const step = (name, kind, v, opts = {}) => {
      const start = cum;
      cum += v;
      rows.push({ name, kind, base: v >= 0 ? start : cum, delta: Math.abs(v), signed: v, cumulative: cum, ...opts });
    };
    const total = (name, kind, v) => rows.push({ name, kind, base: 0, delta: Math.abs(v), signed: v, cumulative: v });

    // Σ Segment-DB (nach Ausschluss-Bereinigung) als ein Startbalken.
    total('Σ Segment-DB', 'segment_sum', data.segment_db_adjusted ?? data.sigma_segment_db ?? 0);
    cum = data.segment_db_adjusted ?? data.sigma_segment_db ?? 0;

    step('+ Sonstige Erträge', 'sonstige_ertrag', data.sonstige_ertraege_block?.total || 0);
    step('− Sonstige Aufwände', 'sonstige_aufwand', data.sonstige_aufwand_block?.total || 0);
    step('− Gemeinkosten', 'gemein', data.gemein_block?.total || 0);
    step('+ GL-Differenz', 'gl', data.gl_differenz || 0, { clickable: true });
    total('Errechnetes EBITDA', 'total', data.errechnetes_ebitda || 0);
    total('GuV-EBITDA', 'guv', data.guv_ebitda || 0);
    return rows;
  }, [data]);

  const monatsData = useMemo(() => {
    if (!data?.monats_trend) return [];
    return data.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1] || `M${m.monat}`,
      'Σ Segment-DB': Math.round(m.segment_db || 0),
      Gemeinkosten: Math.round(m.gemein || 0),
      'Errechnetes EBITDA': Math.round(m.errechnetes_ebitda || 0),
      'GuV-EBITDA': m.guv_ebitda != null ? Math.round(m.guv_ebitda) : null,
    }));
  }, [data]);

  const recon = data?.reconciliation;
  const bannerLevel = recon?.banner?.level || (recon?.ok ? 'green' : 'orange');
  const bannerGreen = bannerLevel === 'green';

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">EBITDA-Wasserfall</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Σ 5 Segment-DB minus Gemeinkosten ergibt errechnetes EBITDA — abgestimmt gegen
            GuV-EBITDA aus dem UGB-Pivot.
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">Fehler: {error}</div>
      )}

      {/* Reconciliation-Banner (#1206 — recon.banner.level/label) */}
      {data && recon && (
        <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
          bannerGreen
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-amber-50 border border-amber-200 text-amber-900'
        }`}>
          <span className="text-base leading-none">{bannerGreen ? '✅' : '⚠️'}</span>
          <span className="font-semibold">
            {recon.banner?.label || (bannerGreen ? 'Reconciliation OK' : 'Hauptbuch-Analytics-Divergenz (Audit prüfen)')}
          </span>
          <span className="text-[#6B7280]">·</span>
          <span className="tabular-nums">
            GL-Differenz {fmtEurDetail(data.gl_differenz)}
          </span>
          {!bannerGreen && (
            <button onClick={() => navigate(`/investor/ebitda-audit?${new URLSearchParams(periodQuery).toString()}`)}
              className="text-xs font-medium underline hover:no-underline ml-1">
              Audit prüfen →
            </button>
          )}
          <span className="text-[11px] text-[#9CA3AF] ml-auto">Toleranz ±{fmtEur(recon.toleranz_eur)}</span>
        </div>
      )}

      {/* KPI-Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Σ Segment-DB" value={fmtEur(data?.sigma_segment_db)}
          sub="Summe der 5 Page-DB" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD" />
        <KpiTile label="Gemeinkosten" value={fmtEur(data?.gemein_block?.total)}
          sub="Verwaltung / MA / Support / Lageropt." loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2" />
        <KpiTile label="Errechnetes EBITDA" value={fmtEur(data?.errechnetes_ebitda)}
          sub={data?.ebitda_marge_pct != null ? `Marge ${fmtPct(data.ebitda_marge_pct)}` : 'Σ Segment-DB − Gemein'}
          loading={loading}
          Icon={BarChart3} iconColor="#10B981" iconTint="#D1FAE5" />
        <KpiTile label="GuV-EBITDA" value={fmtEur(data?.guv_ebitda)}
          sub="UGB-Pivot (investor_ist_values)" loading={loading}
          Icon={Scale} iconColor="#6366F1" iconTint="#E0E7FF" />
      </div>

      {/* Chart-Card mit 3 umschaltbaren Ansichten (#1181) */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">{CHART_MODE_TITLES[chartMode]}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">{CHART_MODE_SUBLABELS[chartMode]}</div>
          </div>
          <ChartModeSwitch mode={chartMode} onChange={setChartMode} />
        </div>
        <div className="p-4">
          {loading && !data && (
            <div className="h-[360px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Odoo-Daten…</div>
          )}
          {data && chartMode === 'flow' && <FlowChart data={data} />}
          {data && chartMode === 'funnel' && <FunnelChart data={data} />}
          {data && chartMode === 'waterfall' && (
            <WaterfallChart
              waterfallData={waterfallData}
              onGlClick={() => navigate(`/investor/ebitda-audit?${new URLSearchParams(periodQuery).toString()}`)}
            />
          )}
        </div>
      </div>

      {/* Segment-DB-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB]">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Segment-Deckungsbeiträge</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Segment</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">DB</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">DB-Marge</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#9CA3AF]">Lade…</td></tr>
              )}
              {data?.segments?.map(s => (
                <tr key={s.code} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                  <td className="px-3 py-2 text-[#1A1A2E]">{s.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(s.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">
                    {s.umsatz ? fmtPct((s.db / s.umsatz) * 100) : '–'}
                  </td>
                </tr>
              ))}
              {data && (
                <tr className="bg-[#F8F9FC] font-semibold border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 text-[#1A1A2E]">Σ Segment-DB</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.sigma_umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.sigma_segment_db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">
                    {data.sigma_umsatz ? fmtPct((data.sigma_segment_db / data.sigma_umsatz) * 100) : '–'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gemeinkosten-KS-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Gemeinkosten-Block</h2>
          {data?.gemein_block && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.gemein_block.lines_processed} Buchungen · KS [6, 7, 8, 45]
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-16">KS-ID</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Kostenstelle</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Σ Buchungen</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Lines</th>
              </tr>
            </thead>
            <tbody>
              {data?.gemein_block?.kostenstellen?.map(k => (
                <tr key={k.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                  <td className="px-3 py-2 text-[#9CA3AF] tabular-nums">#{k.id}</td>
                  <td className="px-3 py-2 text-[#1A1A2E]">{k.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(k.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{k.lines}</td>
                </tr>
              ))}
              {data && (
                <tr className="bg-[#F8F9FC] font-semibold border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 text-[#1A1A2E]" colSpan={2}>Σ Gemein-Block</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.gemein_block.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{data.gemein_block.lines_processed}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Brücke Pfad A → errechnetes EBITDA (#1206) */}
      {data && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Brücke zum errechneten EBITDA</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              6 Stationen von Σ Segment-DB bis errechnetes EBITDA. Die GL-Differenz schließt die
              Lücke zwischen Kostenrechnung (Pfad A) und §231-UGB-Hauptbuch (Pfad B).
            </div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <BridgeRow label="Σ Segment-DB (nach Ausschluss)" value={data.segment_db_adjusted ?? data.sigma_segment_db} />
              <BridgeRow label="+ Sonstige Erträge" value={data.sonstige_ertraege_block?.total}
                meta={(data.sonstige_ertraege_block?.kostenstellen || []).map(k => `#${k.id} ${k.name}`).join(' · ')} />
              <BridgeRow label="− Sonstige Aufwände" value={data.sonstige_aufwand_block?.total}
                meta={(data.sonstige_aufwand_block?.kostenstellen || []).map(k => `#${k.id} ${k.name}`).join(' · ')} />
              <BridgeRow label="− Gemeinkosten" value={data.gemein_block?.total} />
              {(data.include_whitelist?.total || 0) !== 0 && (
                <BridgeRow label="+ Whitelist (KS-lose Konten)" value={data.include_whitelist?.total}
                  meta={(data.include_whitelist?.accounts || []).map(a => `${a.account_code} ${a.account_name || ''}`.trim()).join(' · ')} />
              )}
              <tr className="border-t border-[#F3F4F6] bg-amber-50/40">
                <td className="px-4 py-2">
                  <button onClick={() => navigate(`/investor/ebitda-audit?${new URLSearchParams(periodQuery).toString()}`)}
                    className="text-[#92400E] font-medium hover:underline inline-flex items-center gap-1">
                    + GL-Differenz (Hauptbuch vs. Analytics) →
                  </button>
                  <div className="text-[11px] text-[#9CA3AF] mt-0.5">Klicken öffnet das EBITDA-Audit (Block 4).</div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-medium text-[#92400E]">{fmtEurDetail(data.gl_differenz)}</td>
              </tr>
              <tr className="border-t border-[#E5E7EB] bg-[#F8F9FC] font-semibold">
                <td className="px-4 py-2 text-[#1A1A2E]">= Errechnetes EBITDA</td>
                <td className="px-4 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(data.errechnetes_ebitda)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Monats-Trend */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB]">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Trend</h2>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">
            Σ Segment-DB · Gemeinkosten · errechnetes EBITDA vs. GuV-EBITDA pro Monat.
          </div>
        </div>
        <div className="p-4">
          {loading && !data && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade…</div>
          )}
          {monatsData.length === 0 && !loading && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Monatsdaten.</div>
          )}
          {monatsData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {monatsData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monatsData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                  <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                  <Tooltip formatter={v => fmtEurDetail(v)}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Vorläufige Monate markieren */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Bar dataKey="Σ Segment-DB" fill="#10B981" />
                  <Bar dataKey="Gemeinkosten" fill="#EF4444" />
                  <Bar dataKey="Errechnetes EBITDA" fill="#1A1A2E" />
                  <Bar dataKey="GuV-EBITDA" fill="#6366F1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quellen: 5 Segment-Page-DBs (Outbound/Inbound/Lager/Carrier/Material) · Gemeinkosten aus
        Odoo <code>account.analytic.line</code> KSt [6, 7, 8, 45] · GuV-EBITDA aus
        <code> investor_ist_values</code> (UGB-Pivot, Monats-Sync). Differenz = noch nicht
        zugeordnete Buchungen / Bilanz-Gegenkonten / OPOS → wird via Phase-2 Page-DB-Recompute
        bereinigt.
      </div>
    </div>
  );
}

// ─── Chart-Mode Switcher (#1181) ──────────────────────────────────────────────
const CHART_MODE_TITLES = {
  flow:      'Geldstrom',
  funnel:    'Trichter',
  waterfall: 'Wasserfall',
};
const CHART_MODE_SUBLABELS = {
  flow:      'Wie der Betrag von Umsatz bis EBITDA schrumpft.',
  funnel:    'Segment-Umsätze fließen in den DB-Topf, Gemeinkosten ziehen ab — was übrig bleibt ist EBITDA.',
  waterfall: 'Σ Segment-DB → +Sonstige Erträge → −Sonstige Aufwände → −Gemeinkosten → +GL-Differenz → errechnetes EBITDA. Amber = GL-Differenz (klickbar → EBITDA-Audit).',
};

function ChartModeSwitch({ mode, onChange }) {
  const tabs = [
    { id: 'flow',      label: 'Geldstrom', Icon: BarChartHorizontal },
    { id: 'funnel',    label: 'Trichter',  Icon: Funnel },
    { id: 'waterfall', label: 'Wasserfall', Icon: Droplets },
  ];
  return (
    <div className="bg-[#F8F9FC] rounded-[10px] p-[3px] border border-[#E5E7EB] inline-flex">
      {tabs.map(t => {
        const active = mode === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`px-2.5 py-1 rounded-[7px] text-xs font-medium inline-flex items-center gap-1.5 transition-colors
              ${active ? 'bg-white shadow-sm text-[#1A1A2E] border border-[#E5E7EB]' : 'text-[#6B7280] hover:text-[#1A1A2E]'}`}
            aria-pressed={active}
          >
            <t.Icon width={13} height={13} strokeWidth={2} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Variante C: Geldstrom (DEFAULT, schrumpfende Balken) ─────────────────────
function FlowChart({ data }) {
  const lager = (data.segments || []).find(s => s.code === 'lager');
  const lagerDefAbs = lager && lager.db < 0 ? Math.abs(lager.db) : 0;
  const gemeinAbs   = Math.abs(data.gemein_block?.total || 0);
  const sigmaUmsatz = data.sigma_umsatz || 0;
  const sigmaDb     = data.sigma_segment_db || 0;
  const ebitda      = data.errechnetes_ebitda || 0;
  const margePct    = data.ebitda_marge_pct;

  // Reihen: jede mit Balken-Wert (bar) und Endwert (primary). Annotationen für Abzüge.
  // Balken-Wert ergibt die VISUELLE Breite — Sequenz: Umsatz → (−Lager) → DB → (−Gemein) → EBITDA.
  const rows = [
    { key: 'umsatz',  label: 'Σ Segment-Umsätze',   bar: sigmaUmsatz,                         primary: sigmaUmsatz, kind: 'positive' },
    { key: 'lager',   label: '− Lager-Defizit',     bar: Math.max(0, sigmaUmsatz - lagerDefAbs), primary: null,        annotation: -lagerDefAbs, kind: 'reduction' },
    { key: 'db',      label: 'Σ Deckungsbeitrag',   bar: Math.max(0, sigmaDb),                primary: sigmaDb,     kind: 'subtotal' },
    { key: 'gemein',  label: '− Gemeinkosten',      bar: Math.max(0, sigmaDb - gemeinAbs),    primary: null,        annotation: -gemeinAbs,   kind: 'reduction' },
    { key: 'ebitda',  label: 'EBITDA',              bar: Math.max(0, ebitda),                 primary: ebitda,      margePct,                  kind: 'result' },
  ];
  const maxBar = Math.max(1, ...rows.map(r => r.bar));

  const colors = {
    positive: { bar: '#10B981', text: '#065F46', tint: '#D1FAE5' },
    reduction:{ bar: '#FCA5A5', text: '#7F1D1D', tint: '#FEE2E2' },
    subtotal: { bar: '#338edc', text: '#1E3A8A', tint: '#DBEAFE' },
    result:   { bar: '#1A1A2E', text: '#FFFFFF', tint: '#1A1A2E' },
  };

  return (
    <div className="space-y-2.5 py-2" role="img" aria-label="Geldstrom-Diagramm: Umsatz schrumpft bis EBITDA">
      {rows.map(r => {
        const widthPct = (r.bar / maxBar) * 100;
        const c = colors[r.kind] || colors.positive;
        const isResult = r.kind === 'result';
        const isReduction = r.kind === 'reduction';
        return (
          <div key={r.key} className="flex items-center gap-3 min-h-[40px]">
            {/* Label */}
            <div className="w-[150px] shrink-0 text-right text-sm tabular-nums">
              <span className={`${isReduction ? 'text-[#7F1D1D]' : 'text-[#1A1A2E]'} ${isResult ? 'font-semibold' : ''}`}>
                {r.label}
              </span>
            </div>
            {/* Balken-Spalte */}
            <div className="flex-1 relative">
              <div className="h-9 rounded-md flex items-center px-3 transition-all"
                style={{ width: `${Math.max(2, widthPct)}%`, backgroundColor: c.bar, minWidth: r.primary != null ? 140 : 60 }}>
                {r.primary != null && (
                  <span className="text-sm font-semibold tabular-nums" style={{ color: c.text }}>
                    {fmtEur(r.primary)}
                  </span>
                )}
                {r.primary != null && r.margePct != null && (
                  <span className="ml-2 text-[11px] font-medium tabular-nums" style={{ color: c.text, opacity: 0.85 }}>
                    Marge {fmtPct(r.margePct)}
                  </span>
                )}
              </div>
            </div>
            {/* Annotation (rechts neben Balken) */}
            <div className="w-[140px] shrink-0 text-left text-xs tabular-nums">
              {r.annotation != null && (
                <span className="text-[#B91C1C] font-medium">{fmtEur(r.annotation)}</span>
              )}
            </div>
          </div>
        );
      })}
      <div className="text-[11px] text-[#9CA3AF] mt-2 pl-[162px]">
        Balkenbreite proportional zum verbleibenden Betrag · Rot = Abzug (Lager-Defizit / Gemeinkosten).
      </div>
    </div>
  );
}

// ─── Variante B: Trichter (Pitch-Modus, SVG) ──────────────────────────────────
function FunnelChart({ data }) {
  const lager = (data.segments || []).find(s => s.code === 'lager');
  const lagerDefAbs = lager && lager.db < 0 ? Math.abs(lager.db) : 0;
  const positives = (data.segments || []).filter(s => s.code !== 'lager');
  const gemeinAbs = Math.abs(data.gemein_block?.total || 0);
  const sigmaDb   = data.sigma_segment_db || 0;
  const ebitda    = data.errechnetes_ebitda || 0;
  const margePct  = data.ebitda_marge_pct;

  // SVG-Geometrie
  const W = 800, H = 460;
  const segBoxW = 130, segBoxH = 60;
  const topRowY = 24;
  // 4 Segment-Boxen oben zentriert
  const n = positives.length || 1;
  const totalRowW = n * segBoxW + (n - 1) * 24;
  const startX = (W - totalRowW) / 2;

  // Trichter
  const funnelTopY = 130;
  const funnelTopHalfW = 230;
  const funnelBottomY = 250;
  const funnelBottomHalfW = 90;
  const funnelCenterX = W / 2;

  // EBITDA-Sammelbecken (Quadrat)
  const basinSize = 130;
  const basinX = funnelCenterX - basinSize / 2;
  const basinY = funnelBottomY + 50;

  // Gemeinkosten-Box (rechts, neben dem Trichter)
  const gemeinX = funnelCenterX + funnelTopHalfW + 30;
  const gemeinY = funnelTopY + 30;
  const gemeinW = 150, gemeinH = 70;

  // Lager-Defizit-Box (links, neben dem Trichter)
  const lagerX = funnelCenterX - funnelTopHalfW - 30 - 150;
  const lagerY = funnelTopY + 30;
  const lagerW = 150, lagerH = 60;

  return (
    <div style={{ width: '100%' }} role="img" aria-label="Trichter-Diagramm: Segmente fließen in EBITDA">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: 'auto', maxHeight: 520 }}>
        <defs>
          <marker id="arrow-green" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#10B981" />
          </marker>
          <marker id="arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#EF4444" />
          </marker>
          <marker id="arrow-blue" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#1A1A2E" />
          </marker>
          <linearGradient id="funnelGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#DBEAFE" />
            <stop offset="100%" stopColor="#338edc" />
          </linearGradient>
        </defs>

        {/* Segment-Boxen oben */}
        {positives.map((s, i) => {
          const x = startX + i * (segBoxW + 24);
          const cx = x + segBoxW / 2;
          return (
            <g key={s.code}>
              <rect x={x} y={topRowY} width={segBoxW} height={segBoxH} rx={8}
                fill="#D1FAE5" stroke="#10B981" strokeWidth={1.5} />
              <text x={cx} y={topRowY + 22} textAnchor="middle" fontSize="11" fontWeight="600" fill="#065F46">{s.name}</text>
              <text x={cx} y={topRowY + 44} textAnchor="middle" fontSize="13" fontWeight="700" fill="#065F46" className="tabular-nums">
                {fmtEur(s.db)}
              </text>
              {/* Pfeil nach unten in Richtung Trichter */}
              <line x1={cx} y1={topRowY + segBoxH + 4} x2={funnelCenterX + (cx - funnelCenterX) * 0.35} y2={funnelTopY - 4}
                stroke="#10B981" strokeWidth={1.8} markerEnd="url(#arrow-green)" />
            </g>
          );
        })}

        {/* Lager-Defizit-Box (links) — gestrichelter roter Pfeil VOM Trichter-Eingang WEG (zehrt am Topf) */}
        {lagerDefAbs > 0 && (
          <g>
            <rect x={lagerX} y={lagerY} width={lagerW} height={lagerH} rx={8}
              fill="#FEE2E2" stroke="#EF4444" strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={lagerX + lagerW / 2} y={lagerY + 22} textAnchor="middle" fontSize="11" fontWeight="600" fill="#7F1D1D">Lager-Defizit</text>
            <text x={lagerX + lagerW / 2} y={lagerY + 44} textAnchor="middle" fontSize="13" fontWeight="700" fill="#7F1D1D" className="tabular-nums">
              −{fmtEur(lagerDefAbs)}
            </text>
            {/* Pfeil VON Trichter-Eingang nach links INS Lager-Defizit (zehrt am Topf) */}
            <line x1={funnelCenterX - funnelTopHalfW + 20} y1={funnelTopY + 10} x2={lagerX + lagerW + 4} y2={lagerY + lagerH / 2}
              stroke="#EF4444" strokeWidth={1.5} strokeDasharray="5 3" markerEnd="url(#arrow-red)" />
          </g>
        )}

        {/* Trichter (Trapez) */}
        <polygon
          points={`${funnelCenterX - funnelTopHalfW},${funnelTopY} ${funnelCenterX + funnelTopHalfW},${funnelTopY} ${funnelCenterX + funnelBottomHalfW},${funnelBottomY} ${funnelCenterX - funnelBottomHalfW},${funnelBottomY}`}
          fill="url(#funnelGrad)" stroke="#338edc" strokeWidth={1.5} />
        <text x={funnelCenterX} y={funnelTopY + (funnelBottomY - funnelTopY) / 2 - 4} textAnchor="middle" fontSize="12" fontWeight="600" fill="#1E3A8A">
          Σ Deckungsbeitrag
        </text>
        <text x={funnelCenterX} y={funnelTopY + (funnelBottomY - funnelTopY) / 2 + 18} textAnchor="middle" fontSize="16" fontWeight="700" fill="#1E3A8A" className="tabular-nums">
          {fmtEur(sigmaDb)}
        </text>

        {/* Gemeinkosten-Box (rechts) — roter Pfeil VOM Trichter-Bauch zur Box (zieht ab) */}
        <g>
          <rect x={gemeinX} y={gemeinY} width={gemeinW} height={gemeinH} rx={8}
            fill="#FEE2E2" stroke="#EF4444" strokeWidth={1.5} />
          <text x={gemeinX + gemeinW / 2} y={gemeinY + 22} textAnchor="middle" fontSize="11" fontWeight="600" fill="#7F1D1D">Gemeinkosten</text>
          <text x={gemeinX + gemeinW / 2} y={gemeinY + 44} textAnchor="middle" fontSize="14" fontWeight="700" fill="#7F1D1D" className="tabular-nums">
            −{fmtEur(gemeinAbs)}
          </text>
          <text x={gemeinX + gemeinW / 2} y={gemeinY + 60} textAnchor="middle" fontSize="9" fill="#7F1D1D">Verwaltung · MA · Support · Lageropt.</text>
          {/* Pfeil VOM Trichter NACH Gemein-Box (Abfluss) */}
          <line x1={funnelCenterX + funnelTopHalfW - 30} y1={funnelTopY + 30} x2={gemeinX - 4} y2={gemeinY + gemeinH / 2}
            stroke="#EF4444" strokeWidth={1.8} markerEnd="url(#arrow-red)" strokeDasharray="5 3" />
        </g>

        {/* Pfeil vom Trichter-Boden INS Sammelbecken */}
        <line x1={funnelCenterX} y1={funnelBottomY + 4} x2={funnelCenterX} y2={basinY - 4}
          stroke="#1A1A2E" strokeWidth={2.5} markerEnd="url(#arrow-blue)" />

        {/* EBITDA-Sammelbecken */}
        <rect x={basinX} y={basinY} width={basinSize} height={basinSize} rx={12}
          fill="#1A1A2E" stroke="#1A1A2E" strokeWidth={2} />
        <text x={basinX + basinSize / 2} y={basinY + 32} textAnchor="middle" fontSize="13" fontWeight="600" fill="#FFFFFF">EBITDA</text>
        <text x={basinX + basinSize / 2} y={basinY + 62} textAnchor="middle" fontSize="18" fontWeight="800" fill="#FFFFFF" className="tabular-nums">
          {fmtEur(ebitda)}
        </text>
        {margePct != null && (
          <text x={basinX + basinSize / 2} y={basinY + 86} textAnchor="middle" fontSize="11" fontWeight="500" fill="#A5B4FC" className="tabular-nums">
            Marge {fmtPct(margePct)}
          </text>
        )}
        <text x={basinX + basinSize / 2} y={basinY + 108} textAnchor="middle" fontSize="9" fill="#9CA3AF">Was übrig bleibt</text>
      </svg>
    </div>
  );
}

// ─── Variante A: Klassischer Wasserfall (Bestand, #1180; #1206 6 Stationen) ────
function WaterfallChart({ waterfallData, onGlClick }) {
  const barColor = (kind, signed) => {
    if (kind === 'total') return '#1A1A2E';
    if (kind === 'guv') return '#6366F1';
    if (kind === 'gl') return '#F59E0B';            // GL-Differenz-Brücke = Amber
    if (kind === 'gemein' || kind === 'sonstige_aufwand') return '#EF4444';
    if (kind === 'segment_sum') return '#338edc';
    return signed >= 0 ? '#10B981' : '#EF4444';
  };
  return (
    <div style={{ width: '100%', height: 360 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={waterfallData} margin={{ top: 24, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }} interval={0} angle={-15} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
            tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
          <Tooltip
            formatter={(_v, _name, ctx) => [fmtEurDetail(ctx.payload?.signed), ctx.payload?.name]}
            labelFormatter={() => ''}
            contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
            cursor={{ fill: 'rgba(51,142,220,0.06)' }}
          />
          <Bar dataKey="base" stackId="wf" fill="transparent" />
          <Bar dataKey="delta" stackId="wf" radius={[4,4,0,0]}>
            {waterfallData.map((row, idx) => (
              <Cell key={idx} fill={barColor(row.kind, row.signed)}
                cursor={row.clickable ? 'pointer' : 'default'}
                onClick={row.clickable && onGlClick ? onGlClick : undefined} />
            ))}
            <LabelList dataKey="signed" position="top"
              formatter={(v) => fmtEur(v)}
              style={{ fontSize: 10, fill: '#1A1A2E' }} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function BridgeRow({ label, value, meta }) {
  return (
    <tr className="border-t border-[#F3F4F6]">
      <td className="px-4 py-2">
        <div className="text-[#1A1A2E]">{label}</div>
        {meta && <div className="text-[11px] text-[#9CA3AF] mt-0.5">{meta}</div>}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(value)}</td>
    </tr>
  );
}
